import { defineComponent, ref, onMounted, computed, watch, } from '@vue/composition-api';
import { getSurveyUsers, sendSurveyReminder, } from '@/api/v2/requests/surveys';
import { useExcelExport } from '@/composables/use-excel-export';
import TablePaginationV2 from '@/views/_components/Table/TablePaginationV2.vue';
import TableLoader from '@/views/_components/Table/TableLoader.vue';
import { GenericAGTable } from '@/components/Table/AGTable/AGTable.vue';
import FloatingTextFilter from '@/components/Table/AGTable/_components/FloatingTextFilter.vue';
import FloatingSelectFilter from '@/components/Table/AGTable/_components/FloatingSelectFilter.vue';
import SurveyAnsweredBadge from '@/views/Brand/Surveys/Users/_components/SurveyAnsweredBadge.vue';
import { useSurveyUsersColumns } from '@/views/Brand/Surveys/_composables/use-survey-users-columns';
import { useTableQueryParams } from '@/components/Table/_composables/use-table-query-params';
import FloatingDatepickerFiler from '@/components/Table/AGTable/_components/FloatingDatepickerFilter.vue';
import { Icon, IconSize } from '@/components/Icon';
import { Popover } from '@/components/Popover';
import ShowingMenu from '@/components/EllipsisMenu/ShowingMenu.vue';
import { mdiDotsVertical } from '@mdi/js';
import { useAuthUser } from '@/composables/use-auth-user';
import { useClient } from '@/composables/use-client';
import { useBrand } from '@/composables/use-brand';
import { SystemRole } from '@/api/requests/users';
export default defineComponent({
    name: 'SurveyUsersList',
    components: {
        TablePaginationV2,
        TableLoader,
        Icon,
        Popover,
        ShowingMenu,
        // eslint-disable-next-line vue/no-unused-components
        FloatingTextFilter,
        // eslint-disable-next-line vue/no-unused-components
        FloatingSelectFilter,
        // eslint-disable-next-line vue/no-unused-components
        SurveyAnsweredBadge,
        // eslint-disable-next-line vue/no-unused-components
        FloatingDatepickerFiler,
        AgTable: GenericAGTable(),
    },
    props: {
        surveyId: {
            type: String,
            required: true,
        },
    },
    setup(props, ctx) {
        const isLoadingResponses = ref(false);
        const responses = ref([]);
        const responsesTotalCount = ref(0);
        const agTableRef = ref(null);
        const selectedResponses = ref([]);
        const { hasRole } = useAuthUser();
        const { client } = useClient();
        const { brand } = useBrand();
        const canSelectUsers = computed(() => {
            return (hasRole(SystemRole.SUPER_ADMIN) ||
                hasRole(SystemRole.BRAND_ADMIN) ||
                hasRole(SystemRole.STRUCTURE_MANAGER));
        });
        const { page: responsesPage, perPage: responsesPerPage, query: tableQuery, handleFilterChange: handleQueryFilterChange, handleSort, resetSort, } = useTableQueryParams(ctx, {
            defaultPerPage: 25,
            filterParams: {
                placeNumber: 'placeNumber',
                placeName: 'placeName',
                fullName: 'fullName',
                publishedAt: 'publishedAt',
                isAnswered: 'isAnswered',
                reminderCounter: 'reminderCounter',
            },
            fieldMap: {
                'user.place.number': 'placeNumber',
                'user.place.name': 'placeName',
                'user.fullName': 'fullName',
                publishedAt: 'publishedAt',
                answers: 'isAnswered',
            },
        });
        const responsesQuery = computed(() => {
            return {
                ...tableQuery.value,
            };
        });
        const xlsxColumns = [
            {
                header: ctx.root.$t('brand-surveys.export.client'),
                key: 'clientName',
                width: 20,
            },
            {
                header: ctx.root.$t('brand-surveys.export.brand'),
                key: 'brandName',
                width: 20,
            },
            {
                header: ctx.root.$t('brand-surveys.export.user-id'),
                key: 'userId',
                width: 36,
            },
            {
                header: ctx.root.$t('brand-surveys.details.place-number'),
                key: 'placeNumber',
                width: 15,
            },
            {
                header: ctx.root.$t('brand-surveys.details.place-name'),
                key: 'placeName',
                width: 25,
            },
            {
                header: ctx.root.$t('brand-surveys.details.full-name'),
                key: 'fullName',
                width: 25,
            },
            {
                header: ctx.root.$t('brand-surveys.details.published-at'),
                key: 'publishedAt',
                width: 20,
            },
            {
                header: ctx.root.$t('brand-surveys.details.reminder-count'),
                key: 'reminderCounter',
                width: 15,
            },
            {
                header: ctx.root.$t('brand-surveys.details.answered'),
                key: 'answered',
                width: 15,
            },
        ];
        const { handleExport, isExporting, saveProgress } = useExcelExport({
            columns: xlsxColumns,
            fileNamePrefix: 'survey-users-export',
            dataMapper: (node) => ({
                clientName: client.value?.name || '',
                brandName: brand.value?.name || '',
                userId: node.user?.id || '',
                placeNumber: node.user?.place?.number || '',
                placeName: node.user?.place?.name || '',
                fullName: node.user?.fullName || '',
                publishedAt: node.publishedAt || '',
                reminderCounter: node.reminderCounter || 0,
                answered: node.respondedAt
                    ? ctx.root.$t('brand-surveys.details.answered')
                    : ctx.root.$t('brand-surveys.details.not-answered'),
            }),
        });
        const handleExportAll = () => {
            const currentQuery = responsesQuery.value;
            const dataGetter = async ({ page, perPage, }) => {
                const result = await getSurveyUsers(props.surveyId, {
                    ...currentQuery,
                    sort: currentQuery.sort || '-createdAt',
                    page: Number(page),
                    perPage: Number(perPage),
                });
                return { totalCount: result.totalCount, nodes: result.nodes };
            };
            handleExport(dataGetter);
        };
        const handleExportSelected = () => {
            if (selectedResponses.value.length === 0)
                return;
            const selected = [...selectedResponses.value];
            const dataGetter = async () => {
                return { totalCount: selected.length, nodes: selected };
            };
            handleExport(dataGetter);
        };
        const menuActions = computed(() => ({
            exportAll: {
                name: ctx.root.$t('brand-surveys.export.export-all'),
                handler: handleExportAll,
            },
            exportSelected: {
                name: ctx.root.$t('brand-surveys.export.export-selected', {
                    count: selectedResponses.value.length,
                }),
                handler: handleExportSelected,
                disabled: selectedResponses.value.length === 0,
            },
            sendReminder: {
                name: ctx.root.$t('brand-surveys.users.send-reminder'),
                handler: sendReminderToSelected,
                disabled: selectedResponses.value.length === 0,
            },
        }));
        const handleRowClick = (response) => {
            if (response.user && response.user.id) {
                ctx.root.$router.push({
                    name: 'SurveyUserResponse',
                    params: {
                        id: ctx.root.$route.params.id,
                        surveyId: props.surveyId,
                        userId: response.user.id,
                    },
                });
            }
        };
        const fetchResponses = async () => {
            if (!props.surveyId || isLoadingResponses.value)
                return;
            const apiQuery = responsesQuery.value;
            isLoadingResponses.value = true;
            try {
                const result = await getSurveyUsers(props.surveyId, apiQuery);
                responses.value = result.nodes;
                responsesTotalCount.value = result.totalCount;
            }
            catch (error) {
                ctx.root.$toast.error(ctx.root.$t('brand-surveys.error-fetching-survey-users'));
            }
            finally {
                isLoadingResponses.value = false;
            }
        };
        const changeResponsesPage = (page) => {
            responsesPage.value = page;
        };
        const handleFilterChange = (filterModel) => {
            handleQueryFilterChange(filterModel);
        };
        const handleSortChange = (field, sort) => {
            handleSort(field, sort);
        };
        const handleResetSort = () => {
            resetSort();
        };
        const onSelectionChanged = (selectedRows) => {
            selectedResponses.value = selectedRows;
        };
        const clearSelection = () => {
            selectedResponses.value = [];
            if (agTableRef.value?.gridApi) {
                agTableRef.value.gridApi.deselectAll();
            }
        };
        const sendReminderToSelected = async () => {
            if (selectedResponses.value.length === 0)
                return;
            try {
                const users = selectedResponses.value
                    .map((response) => ({
                    userId: response.user?.id || '',
                    placeId: response.user?.place?.id || '',
                }))
                    .filter((u) => u.userId && u.placeId);
                if (users.length === 0) {
                    ctx.root.$toast.error(ctx.root.$t('brand-surveys.users.no-valid-users-selected'));
                    return;
                }
                await sendSurveyReminder(props.surveyId, users);
                ctx.root.$toast.success(ctx.root.$t('brand-surveys.users.reminder-sent-successfully', {
                    count: users.length,
                }));
                await fetchResponses();
            }
            catch (error) {
                const errorCode = error?.response?.data?.code;
                ctx.root.$toast.error(ctx.root.$t(errorCode === 'PUBLISH_TO_DRAFT'
                    ? 'brand-surveys.users.reminder-draft-error'
                    : 'brand-surveys.users.reminder-send-error'));
            }
            finally {
                clearSelection();
            }
        };
        watch(() => props.surveyId, () => {
            if (props.surveyId) {
                clearSelection();
                fetchResponses();
            }
        }, { immediate: true });
        watch(responsesQuery, () => {
            fetchResponses();
        }, { deep: true });
        onMounted(() => {
            if (props.surveyId) {
                fetchResponses();
            }
        });
        const { columns } = useSurveyUsersColumns(ctx);
        return {
            responsesQuery,
            responsesPage,
            responsesPerPage,
            isLoadingResponses,
            responses,
            responsesTotalCount,
            selectedResponses,
            menuActions,
            changeResponsesPage,
            columns,
            handleRowClick,
            handleFilterChange,
            handleSortChange,
            handleResetSort,
            onSelectionChanged,
            clearSelection,
            sendReminderToSelected,
            agTableRef,
            mdiDotsVertical,
            IconSize,
            canSelectUsers,
            isExporting,
            saveProgress,
        };
    },
});
